/**
 * src/lib/rosterIdentity.ts — track:cc-ai-partners-dynamic (spec seq #43375)
 *
 * Single source of truth for classifying an active_roster slug into a
 * display category for the Team page's AI Partners section
 * (src/components/TeamArea/AITeamSection.tsx). No other file should carry
 * its own slug-name comparisons for this purpose — import and use the
 * result of classifyRosterSlug instead.
 *
 * PLACEHOLDER, pending seq #43369: there is no data model in this
 * workspace that maps a Team Log author slug to a role. `travis` →
 * "human" and `scheduler` → "infrastructure" below are workspace-local
 * guesses, not derived from any roster/membership data — they will
 * misclassify in any other tenant that happens to use these same strings
 * for something else (e.g. an AI partner literally named "scheduler").
 * When the roster-identity model referenced in #43369 lands, this
 * function is the one intended migration point.
 *
 * `relay` and `advisor` are the only two hardcoded values here, and that
 * is intentional, not a stopgap — they are structural constants of the
 * workspace (confirmed directly with Travis, seq #43417), not guesses.
 */

export type PartnerCategory = "pinned" | "ai-partner" | "human" | "infrastructure";

const PINNED_SLUGS: ReadonlySet<string> = new Set(["relay", "advisor"]);

// Workspace-local placeholders only — see file header. Not derived from
// any roster/membership data model; will misclassify in any other
// tenant that happens to use these same strings for an AI partner.
// Deliberately not named PERMANENT_IDS or SYSTEM_IDENTITIES — both names
// already exist elsewhere in this repo meaning something else, and both
// read as settled/authoritative when this is a guess (seq #43417).
const WORKSPACE_LOCAL_SLUG_GUESSES: Readonly<Record<string, PartnerCategory>> = {
  travis: "human",
  scheduler: "infrastructure",
};

export function classifyRosterSlug(slug: string): PartnerCategory {
  if (PINNED_SLUGS.has(slug)) return "pinned";
  const guess = WORKSPACE_LOCAL_SLUG_GUESSES[slug];
  if (guess) return guess;
  return "ai-partner";
}
